import React, { useState, useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { useDataManagement } from '../ferramentas/RequisitarAPI.ts';
import useDebounce from '../ferramentas/useDebounce.js'; // Importando o hook de debounce

// Importando os componentes modularizados
import { ErrorMessage, NoDataMessage } from './components/TableFeedback';
import SortIndicator from './components/SortIndicator';

const ServicosSection = ({ styles, formatCurrency, initialDateRange }) => {
    // Hook para interagir com a API
    const { reports, load, error } = useDataManagement();
    
    // Estado principal de filtros, que dispara a busca na API
    const [filters, setFilters] = useState({
        start_date: initialDateRange.inicio,
        final_date: initialDateRange.fim,
        services: '', // Este valor será atualizado de forma "atrasada"
        page: 1,
        limit: 10,
        sort: { by: 'total', order: 'desc' },
    });

    // --- LÓGICA DE DEBOUNCE ---
    const [serviceInput, setServiceInput] = useState(''); 
    const debouncedServiceInput = useDebounce(serviceInput, 1000); 

    // Estados de controle de UI e dados
    const [reportData, setReportData] = useState(null);
    const [displayedRows, setDisplayedRows] = useState([]);

    // Refs para o gráfico
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    // --- EFEITOS (LÓGICA) ---

    // Observa o valor "atrasado" do input de serviço
    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            services: debouncedServiceInput,
            page: 1 // Reseta para a primeira página ao filtrar
        }));
    }, [debouncedServiceInput]);

    // 1. Busca os dados do relatório sempre que os filtros (filters) mudam
    useEffect(() => {
        const fetchReport = async () => {
            const servicesArray = filters.services 
                ? filters.services.split(',').map(s => s.trim()).filter(Boolean) 
                : [];

            const apiFilters = {
                start_date: filters.start_date || undefined,
                final_date: filters.final_date || undefined,
                services: servicesArray.length > 0 ? servicesArray : undefined,
                page: filters.page,
                limit: parseInt(filters.limit, 10),
                // sort_by: filters.sort.by,
                // sort_order: filters.sort.order,
            };
            
            const response = await reports.services(apiFilters);
            
            if (!response.ok) {
                console.error('Erro ao buscar relatório de serviços:', response.message);
            }
            setReportData(response.ok ? response.data : null);
        };
        fetchReport();
    }, [filters, reports]);

    // 2. Aplica a ordenação local nos dados da página atual
    useEffect(() => {
        if (!reportData || !reportData.data) {
            setDisplayedRows([]);
            return;
        }
        // A API já deve retornar os dados ordenados, mas podemos manter uma ordenação local como fallback
        // ou para quando o usuário clica no header da tabela.
        const sortedPageData = [...reportData.data].sort((a, b) => {
            const valA = a[filters.sort.by];
            const valB = b[filters.sort.by];

            if (typeof valA === 'string') {
                return filters.sort.order === 'asc' 
                    ? valA.localeCompare(valB) 
                    : valB.localeCompare(valA);
            }
            if (valA < valB) return filters.sort.order === 'asc' ? -1 : 1;
            if (valA > valB) return filters.sort.order === 'asc' ? 1 : -1;
            return 0;
        });
        setDisplayedRows(sortedPageData);
    }, [reportData, filters.sort]);

    // 3. Renderiza e atualiza o gráfico
    useEffect(() => {
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }
        if (chartRef.current && reportData?.data?.length > 0) {
            const ctx = chartRef.current.getContext('2d');
            const chartData = [...reportData.data].sort((a, b) => b.total - a.total);
            const chartLabels = chartData.map(item => item.service);
            const chartValues = chartData.map(item => item.total);

            chartInstanceRef.current = new Chart(ctx, {
                type: 'bar',
                data: { 
                    labels: chartLabels, 
                    datasets: [{ 
                        label: 'Faturamento por Serviço', 
                        data: chartValues, 
                        backgroundColor: '#8b5cf6',
                        borderColor: '#a78bfa',
                        borderWidth: 1
                    }] 
                },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    scales: { y: { beginAtZero: true, ticks: { color: '#9ca3af' }, grid: { color: '#374151' } }, x: { ticks: { color: '#9ca3af' }, grid: { color: '#374151' } } },
                    plugins: { legend: { labels: { color: '#d1d5db' } } }
                }
            });
        }
        return () => { if (chartInstanceRef.current) chartInstanceRef.current.destroy(); };
    }, [reportData]);


    // --- MANIPULADORES DE EVENTOS ---
    const handleNonDebouncedFilterChange = (e) => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value, page: 1 }));
    const handleSort = (columnName) => setFilters(prev => ({...prev, sort: {by: columnName, order: prev.sort.by === columnName && prev.sort.order === 'desc' ? 'asc' : 'desc' }, page: 1 }));
    const handleNextPage = () => setFilters(prev => ({ ...prev, page: prev.page + 1 }));
    const handlePreviousPage = () => setFilters(prev => ({ ...prev, page: prev.page - 1 }));

    // --- LÓGICA DE RENDERIZAÇÃO ---
    const totalPages = reportData ? Math.ceil(reportData.total / filters.limit) : 1;
    
    // --- RENDERIZAÇÃO DO COMPONENTE (JSX) ---
    return (
        <div id="servicos" className={`${styles.tabContent} ${styles.activeContent} ${styles.card}`}>
            <h2 className={styles.cardTitle}>Relatório de Faturamento por Serviço</h2>
            
            <div className={`${styles.filterSection} ${styles.colsMd2} ${styles.colsLg4}`}>
                <div>
                    <label>Data Início</label>
                    <input type="date" name="start_date" value={filters.start_date} onChange={handleNonDebouncedFilterChange} />
                </div>
                <div>
                    <label>Data Fim</label>
                    <input type="date" name="final_date" value={filters.final_date} onChange={handleNonDebouncedFilterChange} />
                </div>
                <div>
                    <label>Serviço(s) (separados por vírgula)</label>
                    <input 
                        type="text" 
                        name="services" 
                        value={serviceInput} 
                        onChange={(e) => setServiceInput(e.target.value)}
                        placeholder="Ex: Corte de Cabelo, Barba"
                    />
                </div>
                <div>
                    <label>Itens por Página</label>
                    <select name="limit" value={filters.limit} onChange={handleNonDebouncedFilterChange}>
                        <option value="10">10</option><option value="25">25</option><option value="50">50</option><option value="100">100</option><option value="250">250</option>
                    </select>
                </div>
            </div>

            <div className={styles.tableResponsiveWrapper}>
                <table id="servicos-table" className={`${styles.table} ${styles.tableSortable}`}>
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('service')}>Serviço <SortIndicator column="service" sortConfig={filters.sort} /></th>
                            <th onClick={() => handleSort('qtde')}>Quantidade <SortIndicator column="qtde" sortConfig={filters.sort} /></th>
                            <th onClick={() => handleSort('total')}>Faturamento Total <SortIndicator column="total" sortConfig={filters.sort} /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {load.isLoading ? (
                            <tr><td colSpan="3"><div id="loading-overlay"><div><div id="loading-spinner"></div><p>{load.reason}</p></div></div></td></tr>
                         ) : error ? <ErrorMessage error={error} styles={styles} colSpan={3} />
                         : displayedRows.length > 0 ? (
                            displayedRows.map((row) => (
                                <tr key={row.service}>
                                    <td>{row.service}</td>
                                    <td>{row.qtde}</td>
                                    <td>{formatCurrency(row.total)}</td>
                                </tr>
                            ))
                        ) : <NoDataMessage styles={styles} colSpan={3} />}
                    </tbody>
                    {/* ===== INÍCIO DA SEÇÃO CORRIGIDA ===== */}
                    {!load.isLoading && !error && reportData?.data?.length > 0 && (
                        <tfoot>
                            <tr>
                                {/* Coluna "Serviço": Mostra o total de tipos de serviços com estes filtros */}
                                <td>
                                    Total de {reportData.total} tipos de serviço
                                </td>
                                
                                {/* Coluna "Quantidade": Mostra a soma das quantidades dos itens na página atual */}
                                <td>
                                    {reportData.data.reduce((sum, item) => sum + item.qtde, 0)}
                                </td>

                                {/* Coluna "Faturamento": Mostra o faturamento total do PERÍODO COMPLETO */}
                                <td>
                                    {formatCurrency(reportData.total_financial)} 
                                </td>
                            </tr>
                        </tfoot>
                    )}
                    {/* ===== FIM DA SEÇÃO CORRIGIDA ===== */}
                </table>
            </div>

            {!load.isLoading && !error && reportData && reportData.total > filters.limit && (
                <div className={styles.paginationControls}>
                    <button onClick={handlePreviousPage} disabled={filters.page <= 1}>Anterior</button>
                    <span>Página {filters.page} de {totalPages}</span>
                    <button onClick={handleNextPage} disabled={filters.page >= totalPages}>Próxima</button>
                </div>
            )}
            
            <div className={styles.chartContainer}>
                <canvas ref={chartRef} id="servicosChart"></canvas>
            </div>
        </div>
    );
};

export default ServicosSection;